import * as React from 'react';
import { Shield, Clock, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './Card';
import { Badge } from './Badge';
import { Button } from './Button';
import { cn } from '../utils/cn';
import { getRelativeTime, getTimeRemaining, isExpired } from '../utils';
import type { ApprovalRequest, ApprovalStatus } from '../types';

export interface ApprovalCardProps {
  approval: ApprovalRequest;
  onApprove?: (id: string) => void | Promise<void>;
  onDeny?: (id: string) => void | Promise<void>;
  showActions?: boolean;
  className?: string;
}

const statusConfig: Record<
  ApprovalStatus,
  { icon: React.ReactNode; variant: 'warning' | 'success' | 'destructive' | 'secondary'; label: string }
> = {
  PENDING: {
    icon: <Clock className="h-3.5 w-3.5" />,
    variant: 'warning',
    label: 'Pending',
  },
  APPROVED: {
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    variant: 'success',
    label: 'Approved',
  },
  DENIED: {
    icon: <XCircle className="h-3.5 w-3.5" />,
    variant: 'destructive',
    label: 'Denied',
  },
  EXPIRED: {
    icon: <Clock className="h-3.5 w-3.5" />,
    variant: 'secondary',
    label: 'Expired',
  },
};

const riskConfig: Record<
  ApprovalRequest['risk_level'],
  { icon: React.ReactNode; className: string; label: string }
> = {
  LOW: {
    icon: <Shield className="h-4 w-4" />,
    className: 'text-green-600 dark:text-green-400',
    label: 'Low Risk',
  },
  MEDIUM: {
    icon: <Shield className="h-4 w-4" />,
    className: 'text-yellow-600 dark:text-yellow-400',
    label: 'Medium Risk',
  },
  HIGH: {
    icon: <Shield className="h-4 w-4" />,
    className: 'text-orange-600 dark:text-orange-400',
    label: 'High Risk',
  },
  CRITICAL: {
    icon: <AlertTriangle className="h-4 w-4" />,
    className: 'text-red-600 dark:text-red-400',
    label: 'Critical Risk',
  },
};

export const ApprovalCard = React.memo<ApprovalCardProps>(
  ({ approval, onApprove, onDeny, showActions = true, className }) => {
    const [isApproving, setIsApproving] = React.useState(false);
    const [isDenying, setIsDenying] = React.useState(false);
    const statusInfo = statusConfig[approval.status];
    const riskInfo = riskConfig[approval.risk_level];
    const expired = isExpired(approval.expires_at);
    const timeRemaining = getTimeRemaining(approval.expires_at);

    const handleApprove = async () => {
      if (!onApprove) return;
      setIsApproving(true);
      try {
        await onApprove(approval.id);
      } finally {
        setIsApproving(false);
      }
    };

    const handleDeny = async () => {
      if (!onDeny) return;
      setIsDenying(true);
      try {
        await onDeny(approval.id);
      } finally {
        setIsDenying(false);
      }
    };

    return (
      <Card
        className={cn(
          'transition-all',
          approval.status === 'PENDING' && !expired && 'border-yellow-500/50 shadow-md',
          expired && 'opacity-75',
          className
        )}
      >
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className={cn('flex items-center gap-1', riskInfo.className)}>
                  {riskInfo.icon}
                  <span className="text-xs font-medium">{riskInfo.label}</span>
                </div>
                <Badge variant={statusInfo.variant} className="flex items-center gap-1">
                  {statusInfo.icon}
                  <span>{statusInfo.label}</span>
                </Badge>
              </div>
              <CardTitle className="text-base">{approval.action}</CardTitle>
              <CardDescription className="text-xs">
                Requested by {approval.requested_by}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="rounded-md bg-muted p-3 text-sm">
              <p className="text-foreground">{approval.reason}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="space-y-1">
                <span className="text-muted-foreground">Agent</span>
                <p className="font-medium text-foreground">{approval.agent_name}</p>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground">Requested</span>
                <p className="font-medium text-foreground">
                  {getRelativeTime(approval.requested_at)}
                </p>
              </div>
            </div>
            {approval.status === 'PENDING' && (
              <div
                className={cn(
                  'flex items-center justify-between rounded-md p-2 text-xs',
                  expired
                    ? 'bg-destructive/10 text-destructive'
                    : 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-300'
                )}
              >
                <span className="font-medium">
                  {expired ? 'Expired' : `Expires in ${timeRemaining}`}
                </span>
                {!expired && <Clock className="h-3.5 w-3.5 animate-pulse" />}
              </div>
            )}
            {approval.context && Object.keys(approval.context).length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer font-medium text-muted-foreground hover:text-foreground">
                  View Context
                </summary>
                <pre className="mt-2 rounded-md bg-muted p-2 text-xs overflow-x-auto">
                  {JSON.stringify(approval.context, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </CardContent>
        {showActions && approval.status === 'PENDING' && !expired && (onApprove || onDeny) && (
          <CardFooter className="flex gap-2">
            {onDeny && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeny}
                disabled={isDenying || isApproving}
                className="flex-1"
              >
                <XCircle className="mr-2 h-4 w-4" />
                {isDenying ? 'Denying...' : 'Deny'}
              </Button>
            )}
            {onApprove && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleApprove}
                disabled={isApproving || isDenying}
                className="flex-1"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {isApproving ? 'Approving...' : 'Approve'}
              </Button>
            )}
          </CardFooter>
        )}
      </Card>
    );
  }
);

ApprovalCard.displayName = 'ApprovalCard';
